import initReturnEvents from '../events/return'
import initLoginEvents from '@shared/analytics/events/auth'
import { initCartEvents } from '@shared/analytics/events/cart'
import initListFiltersEvents from '@shared/analytics/events/list-filter'
import { initProductEvents } from '@shared/analytics/events/product'
import initSearchEvents from '@shared/analytics/events/search'
import initShoeSizeFinderEvents from '@shared/analytics/events/shoe-size-finder'
import initStyleSwitcherEvents from '@shared/analytics/events/style-switcher'
import initVirtualPageViewEvents from '@shared/analytics/events/virtual-page-view'
import initWishlistEvents from '@shared/analytics/events/wishlist'
import { addGTMScript, addUsercentricsScript } from '@shared/analytics/scripts'
import type { GoogleEvent } from '@shared/analytics/types/events'
import type { GooglePlugin } from '@shared/analytics/types/google'
import { createUserId } from '@shared/analytics/user'
import { isGtmLoaded, waitForGtmLoaded } from '@shared/fondof-fn/dom/gtm'
import { toCapitalize } from '@shared/fondof-fn/format/string'

import { isInternal } from '@fond-of/env'

export const google: GooglePlugin = {
  /**
   * Save all called dataLayer pushes before gtm got loaded
   */
  events: [],

  /**
   * Accepts custom event and tracks
   * its value in the dataLayer
   */
  event(event: GoogleEvent) {
    if (typeof event === 'string') {
      this.push({
        event,
      })

      return
    }

    this.push(event)
  },

  /**
   * Accepts a custom page type and tracks
   * its value in the dataLayer
   * @param {string} pageType - The type of the page
   */
  page(pageType: string) {
    this.push({
      pageType,
    })
  },

  /**
   * TODO: Discuss if identifier should be more randomized
   *
   * Generates a unique identifier to use
   * as class for tracking purpose
   * @param {string} type - The type of the identifier
   * @param {string} identifier - The identifier
   * @returns {string} - The unique identifier
   */
  getUniqueId: (type, identifier): string => {
    return `gtm${toCapitalize(type)}-${identifier}`
  },

  /**
   * Generates a SHA1 and SHA256 user ID and tracks
   * the user in the dataLayer
   * @param {string} email - The email address of the user
   */
  async user(email) {
    const [userId, userIdV2] = await Promise.all([
      createUserId(email),
      createUserId(email, 'SHA-256'),
    ])

    this.push({
      event: 'setUserId',
      externalIdHash: userId,
      userId,
      userIdv2: userIdV2,
    })
  },

  loadEvents() {
    waitForGtmLoaded(() => {
      this.events.forEach((event) => window.dataLayer.push(event))
    })
  },

  /**
   * Inits the dataLayer, usercentrics script, gtm script
   * and register the analytics events
   * @param {object} options - The options object
   * @returns void
   */
  init(options) {
    window.dataLayer = window.dataLayer || []
    const injectScripts = options?.gtmId && options?.usercentricsId

    if (injectScripts) {
      window.dataLayer.push({
        internalTraffic: isInternal(),
        ...options.initialDataLayer,
      })

      addUsercentricsScript(
        options.usercentricsId,
        options.ucRulesetId,
        options.ucCrossDomainUrl,
        options.ucSdpSettings
      )

      window.addEventListener('ucEvent', ({ detail: { type } }) => {
        // Only load gtm if user explicitly accepts
        if (type === 'implicit') {
          return
        }

        addGTMScript(options.gtmId)

        this.loadEvents()
      })
    }

    if (!injectScripts) {
      this.loadEvents()
    }

    // Register analytics events
    const removeProductEvents = initProductEvents(this)
    const removeCartEvents = initCartEvents(this)
    const removeReturnEvents = initReturnEvents(this)
    const removeLoginEvents = initLoginEvents(this)
    const removeListFiltersEvents = initListFiltersEvents(this)
    const removeWishlistEvents = initWishlistEvents(this)
    const removeStyleSwitcherEvents = initStyleSwitcherEvents(this)
    const removeSearchEvents = initSearchEvents(this)
    const removeShoeSizeFinderEvents = initShoeSizeFinderEvents(this)
    const removeVirtualPageViewEvents = initVirtualPageViewEvents(this)

    return () => {
      removeProductEvents()
      removeCartEvents()
      removeReturnEvents()
      removeLoginEvents()
      removeListFiltersEvents()
      removeWishlistEvents()
      removeStyleSwitcherEvents()
      removeSearchEvents()
      removeShoeSizeFinderEvents()
      removeVirtualPageViewEvents()
    }
  },

  /**
   * Accepts an event payload and either
   * writes to the dayaLayer
   * @param {object} payload - The event payload
   */
  push(payload) {
    if (!isGtmLoaded()) {
      this.events.push(payload)
      return
    }

    window.dataLayer.push(payload)
  },
}
